/**
 * @artossoftware/stoq-sdk — headless storefront SDK for STOQ preorders and
 * back-in-stock signups.
 *
 * Framework-free and SSR-safe: nothing in this module touches `window` or
 * `document` at load time.
 */
import pkg from '../package.json'
import { StoqClient } from './client'
import { emit, EVENT_LOADED } from './events'
import type { StoqConfig } from './types'

export const VERSION: string = pkg.version

/**
 * Loads the shop's settings and selling plans (one Storefront API call,
 * session-cached), then returns a ready-to-use {@link StoqClient}. Fires the
 * documented `stoq:loaded` CustomEvent on `window` (no-op during SSR).
 *
 * Requires `shop` and `storefrontToken` (the shop's PUBLIC Storefront API
 * access token) — see {@link StoqConfig}.
 */
export async function init(config: StoqConfig): Promise<StoqClient> {
  const client = await StoqClient.create(config)
  emit(EVENT_LOADED, {
    pageType: 'sdk',
    enabled: client.settings.enable_app === true,
    settings: client.settings,
    preorderEnabled: client.settings.preorder_enabled === true,
  })
  return client
}

export { StoqClient }
export { StoqApiError, DEFAULT_HOST } from './api'
export { StoqStorefrontApiError } from './metafields'
export { STOQ_COMPONENT_STYLES } from './component-styles'
export { defineStoqElements } from './elements'
export type {
  NotifyMeWidgetOptions,
  PreorderBadgeWidgetOptions,
  PreorderButtonWidgetOptions,
  PreorderWidgetOptions,
  WidgetHandle,
} from './widgets'
export type { VariantAvailability, VariantInput, VariantObjectInput } from './variant-input'
export {
  EVENT_LOADED,
  EVENT_MODAL_CLOSED,
  EVENT_MODAL_OPENED,
  EVENT_PREORDER_MODAL_CLOSED,
  EVENT_PREORDER_MODAL_OPENED,
  EVENT_PREORDER_PAYMENT_OPTION_SELECTED,
  EVENT_SIGNUP_SUBMITTED,
} from './events'
export { DEFAULT_STYLES as DEFAULT_MODAL_STYLES, DEFAULT_TEXTS as DEFAULT_MODAL_TEXTS } from './modal'
export type {
  InlineFormOptions,
  ModalHandle,
  ModalOptions,
  ModalPrefill,
  ModalStyles,
  ModalTexts,
} from './modal'
export { DEFAULT_PREORDER_TEXTS } from './preorder'
export type {
  PreorderConfirmation,
  PreorderButtonContent,
  PreorderModalOptions,
  PreorderModalTexts,
  PreorderPaymentOption,
} from './preorder'
export type {
  CartLine,
  CartLineAttribute,
  SellingPlan,
  SellingPlanPaymentOption,
  SellingPlansResponse,
  SignupInput,
  SignupProductInfo,
  SignupResult,
  StoqConfig,
  StoqSettings,
  VariantPreorderLimits,
  VariantShippingTexts,
  VariantState,
} from './types'
